import React, { useEffect, useRef, useState } from 'react';
import { ClockIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface TimePickerProps {
  /** "HH:mm" vagy üres */
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  /** perc-lépés a gyors választóban (alap: 5) */
  step?: number;
}

/**
 * Saját, sötét módban is jól látszó időpont-választó (a natív <input type="time">
 * felugrója mobilon a rendszer világos sémáját használja).
 */
const TimePicker: React.FC<TimePickerProps> = ({ value = '', onChange, placeholder = '--:--', className = '', step = 5 }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const hourRef = useRef<HTMLDivElement>(null);
  const minuteRef = useRef<HTMLDivElement>(null);

  const [h, m] = (value && /^\d{1,2}:\d{2}$/.test(value)) ? value.split(':').map(Number) : [NaN, NaN];
  const hour = Number.isNaN(h) ? null : h;
  const minute = Number.isNaN(m) ? null : m;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    // aktív érték görgetése a látótérbe
    hourRef.current?.querySelector<HTMLElement>('[data-active="true"]')?.scrollIntoView({ block: 'center' });
    minuteRef.current?.querySelector<HTMLElement>('[data-active="true"]')?.scrollIntoView({ block: 'center' });
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const pad = (n: number) => String(n).padStart(2, '0');
  const set = (hh: number | null, mm: number | null) => {
    const H = hh ?? (hour ?? new Date().getHours());
    const M = mm ?? (minute ?? 0);
    onChange(`${pad(H)}:${pad(M)}`);
  };
  const minutes = Array.from({ length: Math.ceil(60 / step) }, (_, i) => i * step);
  if (minute !== null && !minutes.includes(minute)) {
    minutes.push(minute);
    minutes.sort((a, b) => a - b);
  }

  const col = 'max-h-44 overflow-y-auto [scrollbar-width:thin] flex-1';
  const cell = (active: boolean) => `w-full h-8 text-sm rounded-md ${active ? 'bg-primary-600 text-white font-semibold' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'}`;

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full h-10 rounded-lg border border-[var(--crm-border)] bg-[var(--crm-surface)] px-3 text-sm text-left flex items-center gap-2 hover:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
      >
        <ClockIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
        <span className={`flex-1 ${value ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}`}>{value || placeholder}</span>
        {value && (
          <span role="button" aria-label="Törlés" onClick={(e) => { e.stopPropagation(); onChange(''); }} className="p-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
            <XMarkIcon className="h-4 w-4 text-gray-400" />
          </span>
        )}
      </button>
      {open && (
        <div className="absolute z-50 mt-1 left-0 w-56 rounded-lg border border-[var(--crm-border)] bg-[var(--crm-surface)] shadow-xl p-2">
          <div className="flex gap-2">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1 px-1">Óra</div>
              <div ref={hourRef} className={col}>
                {Array.from({ length: 24 }, (_, i) => (
                  <button key={i} type="button" data-active={hour === i} onClick={() => set(i, null)} className={cell(hour === i)}>{pad(i)}</button>
                ))}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1 px-1">Perc</div>
              <div ref={minuteRef} className={col}>
                {minutes.map(mm => (
                  <button key={mm} type="button" data-active={minute === mm} onClick={() => { set(null, mm); setOpen(false); }} className={cell(minute === mm)}>{pad(mm)}</button>
                ))}
              </div>
            </div>
          </div>
          <div className="flex justify-between items-center mt-2 pt-2 border-t border-[var(--crm-border)]">
            <button type="button" onClick={() => { const d = new Date(); onChange(`${pad(d.getHours())}:${pad(d.getMinutes())}`); setOpen(false); }} className="text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline">Most</button>
            <button type="button" onClick={() => setOpen(false)} className="text-xs font-medium text-gray-600 dark:text-gray-300 hover:underline">Kész</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimePicker;
